'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { TEACHERS } from '@/lib/mockData';
import { CalendarDays, Plus, Minus, ChevronLeft, ChevronRight, RotateCcw, TrendingDown, TrendingUp, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

interface LeaveEntry {
  month: string;
  delta: number;
  reason: string;
  type: 'Annual' | 'Sick' | 'Family' | 'Study' | 'Unpaid' | 'Other';
  enteredBy: string;
}

interface TeacherLeaveBalance {
  teacherId: string;
  annualAllocation: number;
  entries: LeaveEntry[];
}

const LEAVE_TYPES = ['Annual', 'Sick', 'Family', 'Study', 'Unpaid', 'Other'] as const;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function getMonthKey(year: number, month: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function buildInitialBalances(): TeacherLeaveBalance[] {
  return TEACHERS.map((t) => ({
    teacherId: t.id,
    annualAllocation: 15,
    entries: [
      { month: '2026-01', delta: 15, reason: 'Annual allocation', type: 'Annual', enteredBy: 'System' },
      { month: '2026-02', delta: -2, reason: 'Sick leave taken', type: 'Sick', enteredBy: 'Admin: S. Moyo' },
      { month: '2026-03', delta: -1, reason: 'Family emergency', type: 'Family', enteredBy: 'Admin: S. Moyo' },
    ],
  }));
}

function computeBalance(entries: LeaveEntry[], upToMonth?: string): number {
  return entries
    .filter((e) => !upToMonth || e.month <= upToMonth)
    .reduce((sum, e) => sum + e.delta, 0);
}

function getMonthEntries(entries: LeaveEntry[], month: string): LeaveEntry[] {
  return entries.filter((e) => e.month === month);
}

export default function LeaveTrackerPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [balances, setBalances] = useState<TeacherLeaveBalance[]>(buildInitialBalances);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'subtract'>('add');
  const [form, setForm] = useState({ days: 1, reason: '', type: 'Annual' as typeof LEAVE_TYPES[number] });
  const [formError, setFormError] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const monthKey = getMonthKey(year, month);

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  }

  function openModal(teacherId: string, mode: 'add' | 'subtract') {
    setSelectedTeacherId(teacherId);
    setModalMode(mode);
    setForm({ days: 1, reason: '', type: 'Annual' });
    setFormError('');
    setShowModal(true);
  }

  function handleSubmit() {
    if (!form.reason.trim()) { setFormError('Please enter a reason.'); return; }
    if (form.days < 1) { setFormError('Days must be at least 1.'); return; }
    const delta = modalMode === 'add' ? form.days : -form.days;
    setBalances(prev => prev.map(b => {
      if (b.teacherId !== selectedTeacherId) return b;
      return {
        ...b,
        entries: [...b.entries, {
          month: monthKey,
          delta,
          reason: form.reason.trim(),
          type: form.type,
          enteredBy: 'Admin: S. Moyo',
        }],
      };
    }));
    setShowModal(false);
  }

  function resetTeacherMonth(teacherId: string) {
    setBalances(prev => prev.map(b => {
      if (b.teacherId !== teacherId) return b;
      return { ...b, entries: b.entries.filter(e => e.month !== monthKey) };
    }));
  }

  const totalAdded = balances.reduce((sum, b) => {
    const monthEntries = getMonthEntries(b.entries, monthKey);
    return sum + monthEntries.filter(e => e.delta > 0).reduce((s, e) => s + e.delta, 0);
  }, 0);
  const totalUsed = balances.reduce((sum, b) => {
    const monthEntries = getMonthEntries(b.entries, monthKey);
    return sum + Math.abs(monthEntries.filter(e => e.delta < 0).reduce((s, e) => s + e.delta, 0));
  }, 0);
  const lowBalanceCount = balances.filter(b => computeBalance(b.entries, monthKey) <= 3).length;

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="border-b border-border bg-card px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <CalendarDays size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-base font-700 text-foreground leading-tight" style={{ fontWeight: 700 }}>Leave Days Tracker</h1>
              <p className="text-xs text-muted-foreground">Monthly leave balance management per teacher</p>
            </div>
          </div>
          {/* Month Navigator */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="text-sm font-600 text-foreground min-w-[130px] text-center" style={{ fontWeight: 600 }}>
              {MONTH_NAMES[month]} {year}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>

        {/* Summary Strip — stacks on mobile */}
        <div className="grid grid-cols-3 gap-px bg-border border-b border-border">
          <div className="bg-card px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
            <div className="w-7 sm:w-8 h-7 sm:h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <TrendingUp size={14} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wide font-600" style={{ fontWeight: 600 }}>Added</p>
              <p className="text-base sm:text-lg font-700 text-emerald-600 leading-tight" style={{ fontWeight: 700 }}>{totalAdded}</p>
            </div>
          </div>
          <div className="bg-card px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
            <div className="w-7 sm:w-8 h-7 sm:h-8 rounded-lg bg-rose-500/10 flex items-center justify-center shrink-0">
              <TrendingDown size={14} className="text-rose-600" />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wide font-600" style={{ fontWeight: 600 }}>Used</p>
              <p className="text-base sm:text-lg font-700 text-rose-600 leading-tight" style={{ fontWeight: 700 }}>{totalUsed}</p>
            </div>
          </div>
          <div className="bg-card px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
            <div className={`w-7 sm:w-8 h-7 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${lowBalanceCount > 0 ? 'bg-amber-500/10' : 'bg-muted'}`}>
              <AlertTriangle size={14} className={lowBalanceCount > 0 ? 'text-amber-600' : 'text-muted-foreground'} />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wide font-600" style={{ fontWeight: 600 }}>Low ≤3</p>
              <p className={`text-base sm:text-lg font-700 leading-tight ${lowBalanceCount > 0 ? 'text-amber-600' : 'text-foreground'}`} style={{ fontWeight: 700 }}>{lowBalanceCount}</p>
            </div>
          </div>
        </div>

        {/* Table area */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          <div className="rounded-xl border border-border overflow-hidden bg-card">

            {/* ── DESKTOP TABLE (md+) ── */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>Teacher</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>Department</th>
                    <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>Balance</th>
                    <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>This Month</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>Activity — {MONTH_NAMES[month]}</th>
                    <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide" style={{ fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {TEACHERS.map((teacher, idx) => {
                    const balance = balances.find(b => b.teacherId === teacher.id);
                    const currentBalance = balance ? computeBalance(balance.entries, monthKey) : 0;
                    const monthEntries = balance ? getMonthEntries(balance.entries, monthKey) : [];
                    const monthNet = monthEntries.reduce((s, e) => s + e.delta, 0);
                    const isLow = currentBalance <= 3;
                    const isNegative = currentBalance < 0;

                    return (
                      <tr
                        key={teacher.id}
                        className={`border-b border-border last:border-0 transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-700 text-primary" style={{ fontWeight: 700 }}>
                                {teacher.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                              </span>
                            </div>
                            <div>
                              <p className="font-500 text-foreground text-xs leading-tight" style={{ fontWeight: 500 }}>{teacher.name}</p>
                              <p className="text-[10px] text-muted-foreground">{teacher.employeeNumber}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">{teacher.department}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-10 h-7 rounded-lg text-sm font-700 ${
                            isNegative ? 'bg-rose-100 text-rose-700' : isLow ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                          }`} style={{ fontWeight: 700 }}>
                            {currentBalance}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {monthNet === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <span className={`text-xs font-600 ${monthNet > 0 ? 'text-emerald-600' : 'text-rose-600'}`} style={{ fontWeight: 600 }}>
                              {monthNet > 0 ? `+${monthNet}` : monthNet}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {monthEntries.length === 0 ? (
                            <span className="text-xs text-muted-foreground italic">No activity</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {monthEntries.map((entry, i) => (
                                <div key={i} className="flex items-center gap-1.5">
                                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-700 shrink-0 ${
                                    entry.delta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                                  }`} style={{ fontWeight: 700 }}>
                                    {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground truncate">{entry.reason}</span>
                                  <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-auto pl-2">({entry.type})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openModal(teacher.id, 'add')}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 transition-colors text-xs font-600"
                              style={{ fontWeight: 600 }}
                            >
                              <Plus size={12} />Add
                            </button>
                            <button
                              type="button"
                              onClick={() => openModal(teacher.id, 'subtract')}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 transition-colors text-xs font-600"
                              style={{ fontWeight: 600 }}
                            >
                              <Minus size={12} />Use
                            </button>
                            {monthEntries.length > 0 && (
                              <button
                                type="button"
                                onClick={() => resetTeacherMonth(teacher.id)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <RotateCcw size={11} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── MOBILE STACKED CARDS (< md) ── */}
            <div className="md:hidden divide-y divide-border">
              {TEACHERS.map((teacher, idx) => {
                const balance = balances.find(b => b.teacherId === teacher.id);
                const currentBalance = balance ? computeBalance(balance.entries, monthKey) : 0;
                const monthEntries = balance ? getMonthEntries(balance.entries, monthKey) : [];
                const monthNet = monthEntries.reduce((s, e) => s + e.delta, 0);
                const isLow = currentBalance <= 3;
                const isNegative = currentBalance < 0;
                const isExpanded = expandedRow === teacher.id;

                return (
                  <div key={`mob-${teacher.id}`} className={`px-4 py-3 ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}>
                    {/* Primary row */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-700 text-primary" style={{ fontWeight: 700 }}>
                            {teacher.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-600 text-foreground truncate" style={{ fontWeight: 600 }}>{teacher.name}</p>
                          <p className="text-xs text-muted-foreground">{teacher.department}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Balance badge */}
                        <span className={`inline-flex items-center justify-center w-9 h-7 rounded-lg text-sm font-700 ${
                          isNegative ? 'bg-rose-100 text-rose-700' : isLow ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                        }`} style={{ fontWeight: 700 }}>
                          {currentBalance}
                        </span>
                        <button
                          type="button"
                          onClick={() => setExpandedRow(isExpanded ? null : teacher.id)}
                          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                    </div>

                    {/* Quick action buttons always visible */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => openModal(teacher.id, 'add')}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 transition-colors text-xs font-600 flex-1 justify-center"
                        style={{ fontWeight: 600 }}
                      >
                        <Plus size={12} />Add
                      </button>
                      <button
                        type="button"
                        onClick={() => openModal(teacher.id, 'subtract')}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 transition-colors text-xs font-600 flex-1 justify-center"
                        style={{ fontWeight: 600 }}
                      >
                        <Minus size={12} />Use
                      </button>
                      {monthEntries.length > 0 && (
                        <button
                          type="button"
                          onClick={() => resetTeacherMonth(teacher.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <RotateCcw size={11} />
                        </button>
                      )}
                    </div>

                    {/* Expanded: month net + activity log */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">This month net:</span>
                          {monthNet === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <span className={`text-xs font-600 ${monthNet > 0 ? 'text-emerald-600' : 'text-rose-600'}`} style={{ fontWeight: 600 }}>
                              {monthNet > 0 ? `+${monthNet}` : monthNet} days
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Activity — {MONTH_NAMES[month]}</p>
                          {monthEntries.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">No activity this month</p>
                          ) : (
                            <div className="space-y-1">
                              {monthEntries.map((entry, i) => (
                                <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-lg px-2 py-1.5">
                                  <span className={`inline-flex items-center justify-center w-6 h-5 rounded text-[10px] font-700 shrink-0 ${
                                    entry.delta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                                  }`} style={{ fontWeight: 700 }}>
                                    {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                                  </span>
                                  <span className="text-xs text-muted-foreground flex-1 truncate">{entry.reason}</span>
                                  <span className="text-[10px] text-muted-foreground/60 shrink-0">({entry.type})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedTeacherId && (() => {
        const teacher = TEACHERS.find(t => t.id === selectedTeacherId);
        const balance = balances.find(b => b.teacherId === selectedTeacherId);
        const currentBalance = balance ? computeBalance(balance.entries, monthKey) : 0;
        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm overflow-hidden">
              {/* Modal Header */}
              <div className={`px-5 py-4 border-b border-border flex items-center gap-3 ${modalMode === 'add' ? 'bg-emerald-500/5' : 'bg-rose-500/5'}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${modalMode === 'add' ? 'bg-emerald-500/15' : 'bg-rose-500/15'}`}>
                  {modalMode === 'add' ? <Plus size={16} className="text-emerald-600" /> : <Minus size={16} className="text-rose-600" />}
                </div>
                <div>
                  <p className="text-sm font-700 text-foreground" style={{ fontWeight: 700 }}>
                    {modalMode === 'add' ? 'Add Leave Days' : 'Record Leave Used'}
                  </p>
                  <p className="text-xs text-muted-foreground">{teacher?.name} · Balance: {currentBalance} days</p>
                </div>
              </div>
              {/* Modal Body */}
              <div className="px-5 py-4 space-y-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
                  <CalendarDays size={13} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-500" style={{ fontWeight: 500 }}>
                    Month: {MONTH_NAMES[month]} {year}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5" style={{ fontWeight: 600 }}>Number of Days</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, days: Math.max(1, f.days - 1) }))}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      <Minus size={13} />
                    </button>
                    <input
                      type="number"
                      min={1}
                      value={form.days}
                      onChange={e => setForm(f => ({ ...f, days: Math.max(1, parseInt(e.target.value) || 1) }))}
                      className="flex-1 text-center border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, days: f.days + 1 }))}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5" style={{ fontWeight: 600 }}>Leave Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value as typeof LEAVE_TYPES[number] }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5" style={{ fontWeight: 600 }}>Reason / Note</label>
                  <input
                    type="text"
                    placeholder={modalMode === 'add' ? 'e.g. Annual allocation, carry-over...' : 'e.g. Sick leave taken, family emergency...'}
                    value={form.reason}
                    onChange={e => { setForm(f => ({ ...f, reason: e.target.value })); setFormError(''); }}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {formError && <p className="text-xs text-rose-600 mt-1">{formError}</p>}
                </div>
              </div>
              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-border flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`px-4 py-2 rounded-lg text-sm font-600 text-white transition-colors ${
                    modalMode === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                  style={{ fontWeight: 600 }}
                >
                  {modalMode === 'add' ? 'Add Days' : 'Record Usage'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </AppLayout>
  );
}
